/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderNavigationService, FilterNames, FilterRestService } from '@gh/builder-shared';
import {
  AelEvents,
  BuilderPermissionSet,
  getAelEventsArray,
  ScheduleDetailsDto,
  ScheduleType,
} from '@gh/builder-shared-data';
import {
  cellValueFormatter,
  rendererIconByName,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import {
  AppRouter,
  DateTimeTypes,
  EnumDictionary,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getAllNewSelectedEntities,
  getRouteInitData,
  LocalizationBusinessService,
  UserContext,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import {
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildDateTimeColumn,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { filter$, map$, switchMap$, debounceTime$, skip$, takeUntil$ } from '@gh/rx/operators';
import { pick } from 'lodash';
import { Subject } from 'rxjs';
import {
  DATE_FILTER,
  ScheduleFinderDialogConfig,
} from '../../resources/schedule-finder-business.service';
import { ScheduleFinderListItem } from '../../resources/schedule-finder.model';
import {
  JobScheduleCreateDialogConfig,
  ScheduleListBusinessService,
} from '../../resources/schedule-list-business.service';
import {
  PreConstructionScheduleFilter,
  PreConstructionScheduleListItem,
} from '../../resources/schedule-list.model';
import { jobScheduleCreateDialogRoute } from '../job-schedule-create-dialog/job-schedule-create-dialog.component';
import { scheduleFinderDialogRoute } from '../schedule-finder-dialog/schedule-finder-dialog.route';
import { ID } from 'common-shared-data/common';

const FILTER_PERSIST_DEBOUNCE_MS = 300;

@Component({
  standalone: false,
  selector: 'gh-pre-construction-schedule-list',
  templateUrl: 'pre-construction-schedule-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreConstructionScheduleListComponent implements OnInit, OnDestroy {
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();
  dataSource: SlicedDataSource<PreConstructionScheduleListItem>;
  form: FilterableFormState<PreConstructionScheduleFilter>;
  filter: any;
  dateFilter = DATE_FILTER;
  private readonly destroy$ = new Subject<void>();
  private scheduleTypeDict: EnumDictionary<ScheduleType>;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private userContext: UserContext,
              private routeData: RouteDataService,
              private formStateBuilder: FormStateBuilder,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private waitings: WaitingService,
              private filterService: FilterRestService,
              private navigations: BuilderNavigationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private localizationBusinessService: LocalizationBusinessService,
              private business: ScheduleListBusinessService,
              private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    const { filter: initFilter, gridLayout, scheduleTypeDict } = getRouteInitData(this.route);
    this.scheduleTypeDict = scheduleTypeDict;
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        jobNumber: {
          // list always gets sorted from BE
          comparator: () => 0,
        },
        startDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        endDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        isTodoExist: { cellRenderer: truthyCellRenderer(rendererIconByName('done')) },
        duration: {
          valueFormatter: cellValueFormatter<number>((duration) =>
            this.localizationBusinessService.formatHoursDuration(duration)),
        },
      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        getRowId: (params) => String(params.data.scheduleId)
      });
    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: {
        ...initFilter,
        authToCommenceDate: initFilter.authToCommenceDate || 'ALL',
      },
      children: {
        authToCommenceDate: void 0,
        searchString: void 0,
        showActive: void 0,
        showFinalized: void 0,
        showNotProceeding: void 0,
        showDpp: void 0,
        showMyJobs: void 0,
        showCompleted: void 0,
      },
    }));

    const tracker = this.routeData
      .tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.pipe(
      takeUntil$(this.destroy$),
    ).subscribe(([filter]) => {
      this.filter = filter;
      this.dataSource = this.business.findAllPreConstructionSchedulesAsDataSource(this.filter);
      this.changeDetectorRef.markForCheck();
    });
    tracker.filterChanges.pipe(
      debounceTime$(FILTER_PERSIST_DEBOUNCE_MS),
      skip$(1),
      switchMap$(([filter]) => this.filterService.update(FilterNames.preconstructionSchedule, filter)),
      takeUntil$(this.destroy$),
    ).subscribe({ error: this.notifications.processAll });
  }

  onCreate(): void {
    this.dialogs.openWithInput(jobScheduleCreateDialogRoute, <JobScheduleCreateDialogConfig>{
        type: ScheduleType.PreConstruction,
        helpId: 'schedules/pre-constructions/details',
      }).afterClosed().pipe(
        filter$(Boolean))
      .subscribe((init: { jobId: ID; templateId: ID }) => {
        this.business.init(init.jobId, init.templateId, ScheduleType.PreConstruction)
          .subscribe(
            (scheduleDetails: ScheduleDetailsDto) => {
              if (scheduleDetails.errorMessage) {
                const cyclicIssuesSummary = scheduleDetails.errorMessage
                this.notifications.warning("msg_cyclic_depedency", { issues: cyclicIssuesSummary });
              } else {
                this.router.navigateToChild(['schedules/pre-construction/new/gantt-chart'], {
                  queryParams: pick(init, 'jobId', 'templateId'),
                });
              }
            },
            this.notifications.processAll,
          );
      });
  }
  

  onSearch(): void {
    this.form.emitFilter();
  }

  goToAel(schedule?: PreConstructionScheduleListItem): void {
    this.navigations.goToAel({
      label: 'lbl_pre_construction_schedules',
      entityType: 'JOB',
      entityId: schedule ? schedule.jobId : void 0,
      eventTypeIds: getAelEventsArray(AelEvents.Job.PreConstructionSchedule),
    });
  }

  onOpenCombinedGanttChart(): void {
    this.dialogs.openWithInput(scheduleFinderDialogRoute, <ScheduleFinderDialogConfig>{
      type: ScheduleType.PreConstruction,
      multiselection: true,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((items: ScheduleFinderListItem[]) => {
        this.router.navigateToChild(['schedules/pre-construction/combined/gantt-chart'], {
          queryParams: { scheduleIds: items.map((item) => item.scheduleId).join() },
        });
      });
  }

  onOpen(schedule: PreConstructionScheduleListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Schedules.PreConstruction.EDIT_VIEW)) {
      this.router.navigateToChild(['schedules/pre-construction', schedule.scheduleId, 'gantt-chart']);
    }
  }

  onRemove(schedule: PreConstructionScheduleListItem): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_sch_delete', {
      schType: this.scheduleTypeDict.get(ScheduleType.PreConstruction),
      jobNumber: schedule.jobNumber,
    })).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.business.removePreConstructionSchedule(schedule).pipe(this.waitings.operator())))
      .subscribe(
        () => this.loadData(),
        this.notifications.processAll);
  }

  onOpenWorksheet(): void {
    this.router.navigateToChild(['schedules/pre-construction/worksheet'], {
      queryParams: this.form.value,
    });
  }

  private loadData(): void {
    this.dataSource = this.business.findAllPreConstructionSchedulesAsDataSource(this.filter);
  }
}
